import api from '@/services/api.service';

/** Slim quotation summary when API loads `relations: ['quotation']`. */
export interface ContractReviewQuotationSummary {
  id: string;
  quotation_number: string;
  quotation_name?: string;
  status?: string;
  revision_number?: number;
}

export interface ContractReview {
  id: string;
  company_id: string;
  review_number: string;
  review_date: string;
  quotation_id: string;
  revenue_service_type_id?: string | null;
  /** Populated on GET detail for display (not stored on row). */
  quotation?: ContractReviewQuotationSummary | null;
  customer_id?: string | null;
  opportunity_id?: string | null;
  project_id?: string | null;
  sales_person?: string | null;
  review_status: string;
  approval_status: string;
  prepared_by?: string | null;
  reviewed_by?: string | null;
  remarks?: string | null;
  approval_instance_id?: string | null;
  sections?: Record<string, unknown> | null;
  overall_risk_rating?: string | null;
  final_recommendation?: string | null;
  quotation_snapshot_hash?: string | null;
  quotation_revision_number_snapshot?: number | null;
  is_stale?: boolean;
  stale_reason?: string | null;
  conditions_text?: string | null;
  conditions_acknowledged_at?: string | null;
  conditions_acknowledged_by?: string | null;
  approved_at?: string | null;
  sales_order_id?: string | null;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
  created_at?: string;
  updated_at?: string;
}

const base = '/crm/contract-reviews';

function peelData<T>(body: unknown): T | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const w = body as { data?: T };
  return w.data !== undefined ? w.data : (body as T);
}

export async function listContractReviews(params: Record<string, string | undefined>): Promise<{
  data: ContractReview[];
  total: number;
}> {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') q.set(k, v);
  });
  const res = await api.get<ContractReview[]>(`${base}?${q.toString()}`);
  const rows = Array.isArray(res.data) ? res.data : [];
  const total = typeof res.meta?.total === 'number' ? res.meta.total : rows.length;
  return { data: rows, total };
}

export async function getContractReview(id: string): Promise<ContractReview> {
  const res = await api.get(`${base}/${id}`);
  const body = (res as any)?.data ?? res;
  const row = peelData<ContractReview>(body) ?? (body as ContractReview);
  return row;
}

export async function listByQuotation(quotationId: string): Promise<ContractReview[]> {
  const res = await api.get(`${base}/by-quotation/${quotationId}`);
  const body = (res as any)?.data ?? res;
  const rows = peelData<ContractReview[]>(body) ?? (Array.isArray(body) ? body : []);
  return Array.isArray(rows) ? rows : [];
}

export async function createFromQuotation(quotationId: string): Promise<ContractReview> {
  const res = await api.post(`${base}/from-quotation/${quotationId}`, {});
  const body = (res as any)?.data ?? res;
  return peelData<ContractReview>(body) ?? (body as ContractReview);
}

export async function updateContractReview(
  id: string,
  patch: Partial<{
    review_date: string;
    remarks: string;
    sections: Record<string, unknown>;
    overall_risk_rating: string;
    final_recommendation: string;
    conditions_text: string;
    reviewed_by: string;
    revenue_service_type_id: string | null;
  }>
): Promise<ContractReview> {
  const res = await api.put(`${base}/${id}`, patch);
  const body = (res as any)?.data ?? res;
  return peelData<ContractReview>(body) ?? (body as ContractReview);
}

export async function submitContractReview(id: string): Promise<ContractReview> {
  const res = await api.post(`${base}/${id}/submit-for-approval`, {});
  const body = (res as any)?.data ?? res;
  return peelData<ContractReview>(body) ?? (body as ContractReview);
}

export async function approveContractReview(id: string, remarks?: string): Promise<ContractReview> {
  const res = await api.post(`${base}/${id}/approve`, { remarks });
  const body = (res as any)?.data ?? res;
  return peelData<ContractReview>(body) ?? (body as ContractReview);
}

export async function rejectContractReviewUniversal(
  id: string,
  payload: { rejection_reason_code: string; remarks?: string }
): Promise<ContractReview> {
  const res = await api.post(`${base}/${id}/reject-universal-approval`, payload);
  const body = (res as any)?.data ?? res;
  return peelData<ContractReview>(body) ?? (body as ContractReview);
}

export async function acknowledgeConditions(id: string): Promise<ContractReview> {
  const res = await api.post(`${base}/${id}/acknowledge-conditions`, {});
  const body = (res as any)?.data ?? res;
  return peelData<ContractReview>(body) ?? (body as ContractReview);
}

export async function cancelContractReview(id: string): Promise<ContractReview> {
  const res = await api.post(`${base}/${id}/cancel`, {});
  const body = (res as any)?.data ?? res;
  return peelData<ContractReview>(body) ?? (body as ContractReview);
}

const contractReviewService = {
  list: listContractReviews,
  get: getContractReview,
  listByQuotation,
  createFromQuotation,
  update: updateContractReview,
  submit: submitContractReview,
  approve: approveContractReview,
  rejectUniversal: rejectContractReviewUniversal,
  acknowledgeConditions,
  cancel: cancelContractReview,
};

export default contractReviewService;
